<template>
  <div
    class="bg-white dark:bg-gray-900 bg-cover bg-center bg-no-repeat flex-grow"
    :style="{
      backgroundImage: themeStore.isDark
        ? 'url(/background/jb-bg-dark.webp)'
        : 'url(/background/jbc-bg-light.webp)'
    }"
  >
    <div class="container mx-auto px-4 py-12 md:py-20">
      <!-- Hero Section -->
      <div class="text-center mb-20 relative">
        <img
          :src="themeStore.isDark ? '/logo/jbc-ticket-dark.png' : '/logo/jbc-ticket-light.png'"
          alt="Jukebox Concerts Logo"
          class="mx-auto title-image"
        />
        <h1 class="text-5xl md:text-7xl font-bold text-gray-700 dark:text-white mb-6 leading-tight">
          Jukebox <span class="title-highlight">Concerts</span>
        </h1>
      </div>

      <!-- Event Code Input Section -->
      <div class="max-w-lg mx-auto mb-8">
        <div
          class="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-8 md:p-12 border border-gray-200 dark:border-gray-700 shadow-sm"
        >
          <h2 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 text-center">
            {{ $t("home.joinEvent") }}
          </h2>
          <p class="text-gray-600 dark:text-white mb-8 text-center">
            {{ $t("home.enterCode") }}
          </p>

          <form class="space-y-6" @submit.prevent="handleSubmit">
            <div class="relative">
              <InputText
                v-model="eventCode"
                :placeholder="$t('home.codePlaceholder')"
                :maxlength="6"
                class="w-full text-center text-3xl font-mono tracking-[0.5em] py-4 border-2 focus:border-teal-500 dark:focus:border-teal-400"
                :class="{ 'p-invalid border-red-500 dark:border-red-400': error }"
                @input="handleCodeInput"
              />
              <small
                v-if="error"
                class="mt-3 block text-center text-sm text-red-600 dark:text-red-400"
                >{{ error }}</small
              >
            </div>

            <Button
              type="submit"
              :label="$t('home.joinButton')"
              icon="pi pi-arrow-right"
              icon-pos="right"
              :loading="loading"
              class="w-full py-3 text-lg font-semibold"
              severity="primary"
            />
          </form>
        </div>
      </div>

      <!-- How It Works Link -->
      <div class="text-center">
        <button
          class="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium text-md transition-colors underline decoration-2 underline-offset-4"
          @click="showHowItWorksModal = true"
        >
          {{ $t("home.howItWorks") }}
        </button>
      </div>
    </div>

    <!-- How It Works Modal -->
    <Dialog
      v-model:visible="showHowItWorksModal"
      modal
      :dismissable-mask="true"
      :draggable="false"
      :header="$t('home.howItWorksTitle')"
      :style="{ width: '90vw', maxWidth: '600px' }"
      :pt="{
        root: { class: 'dark:bg-gray-900' },
        header: { class: 'dark:bg-gray-900 dark:text-white font-heading rounded-t-xl' },
        content: { class: 'dark:bg-gray-900 dark:text-gray-200 rounded-b-xl' },
        closeButton: { class: 'dark:hover:bg-gray-800 dark:text-gray-400 dark:hover:text-gray-200' }
      }"
    >
      <div class="space-y-6">
        <div class="flex items-start gap-4">
          <div
            class="flex-shrink-0 w-12 h-12 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center"
          >
            <i class="pi pi-mobile text-2xl text-teal-600 dark:text-teal-400"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {{ $t("home.step1Title") }}
            </h3>
            <p class="text-gray-700 dark:text-gray-200">
              {{ $t("home.step1Description") }}
            </p>
          </div>
        </div>

        <div class="flex items-start gap-4">
          <div
            class="flex-shrink-0 w-12 h-12 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center"
          >
            <i class="pi pi-check-square text-2xl text-teal-600 dark:text-teal-400"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {{ $t("home.step2Title") }}
            </h3>
            <p class="text-gray-700 dark:text-gray-200">
              {{ $t("home.step2Description") }}
            </p>
          </div>
        </div>

        <div class="flex items-start gap-4">
          <div
            class="flex-shrink-0 w-12 h-12 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center"
          >
            <i class="pi pi-play text-2xl text-teal-600 dark:text-teal-400"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {{ $t("home.step3Title") }}
            </h3>
            <p class="text-gray-700 dark:text-gray-200">
              {{ $t("home.step3Description") }}
            </p>
          </div>
        </div>

        <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
          <p class="text-sm text-gray-600 dark:text-gray-300 text-center">
            <i class="pi pi-info-circle mr-2"></i>
            {{ $t("home.infoText") }}
          </p>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
// import { useToast } from "primevue/usetoast";
import { useThemeStore } from "../stores/theme";
import api from "../services/api";

const router = useRouter();
// const toast = useToast()
const themeStore = useThemeStore();
const { t } = useI18n();

const eventCode = ref("");
const loading = ref(false);
const error = ref("");
const showHowItWorksModal = ref(false);

const handleCodeInput = () => {
  // Only allow digits
  eventCode.value = eventCode.value.replace(/\D/g, "");
  error.value = "";
};

const handleSubmit = async () => {
  error.value = "";
  loading.value = true;

  if (!eventCode.value) {
    error.value = t("home.errors.enterCode");
    loading.value = false;
    return;
  }

  if (eventCode.value.length !== 6) {
    error.value = t("home.errors.codeLength");
    loading.value = false;
    return;
  }

  try {
    // Fetch event by code to get the slug
    const response = await api.get(`/api/events/code/${eventCode.value}`);

    if (response.data.success && response.data.data.event) {
      const event = response.data.data.event;

      // Check if event has a slug
      if (event.slug) {
        // Redirect to slug-based URL
        router.push(`/${event.slug}`);
      } else {
        error.value = t("home.errors.noSlug");
      }
    } else {
      error.value = t("home.errors.eventNotFound");
    }
  } catch (err) {
    if (err.response?.status === 404) {
      error.value = t("home.errors.eventNotFound");
    } else {
      error.value = t("home.errors.loadFailed");
    }
  } finally {
    loading.value = false;
  }
};
</script>
<style scoped>
.title-highlight {
  display: inline-block;
  color: #00ffff;
  text-transform: uppercase;
  font-size: 60%;
  position: absolute;
  width: 100%;
  letter-spacing: 6px;
  left: 5px;
  right: 0;
  top: 70px;
}
@media (max-width: 768px) {
  .title-highlight {
    top: 50px;
    letter-spacing: 4px;
    left: 2px;
  }
}
.title-image {
  position: absolute;
  left: 0;
  right: 242px;
  top: -21px;
  width: 60px;
  height: 60px;
  transform: rotate(-95deg);
}
@media (max-width: 768px) {
  .title-image {
    width: 40px;
    height: 40px;
    top: -15px;
    right: 150px;
  }
}
</style>
